import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AutomaticCarryoverKPI } from '@/components/dashboard/AutomaticCarryoverKPI';
import { DailySalesKPI } from '@/components/dashboard/DailySalesKPI';
import { InventoryMovementsPanel } from '@/components/dashboard/InventoryMovementsPanel';
import { LateOpeningTracker } from '@/components/dashboard/LateOpeningTracker';
import { RoleBasedAccessSummary } from '@/components/dashboard/RoleBasedAccessSummary';
import { 
  Target, 
  Wallet, 
  Package, 
  AlertTriangle, 
  Settings,
  TrendingUp
} from 'lucide-react';

interface BusinessIntelligencePanelProps {
  showAccessDetails?: boolean;
  onToggleAccessDetails?: () => void;
}

export function BusinessIntelligencePanel({ 
  showAccessDetails = false, 
  onToggleAccessDetails = () => {} 
}: BusinessIntelligencePanelProps) {
  const [activeTab, setActiveTab] = useState('target');

  const tabs = [
    {
      id: 'target',
      label: 'Mi Meta',
      icon: Target,
      component: <AutomaticCarryoverKPI />
    },
    {
      id: 'sales',
      label: 'Ventas',
      icon: Wallet,
      component: <DailySalesKPI />
    },
    {
      id: 'inventory',
      label: 'Inventario',
      icon: Package,
      component: <InventoryMovementsPanel />
    },
    {
      id: 'discipline',
      label: 'Aperturas',
      icon: AlertTriangle,
      component: <LateOpeningTracker />
    },
    {
      id: 'access',
      label: 'Acceso',
      icon: Settings,
      component: (
        <RoleBasedAccessSummary 
          showDetails={showAccessDetails}
          onToggleDetails={onToggleAccessDetails}
        />
      )
    }
  ];

  return (
    <Card className="apple-card bg-card/40 backdrop-blur-xl border-border/20 shadow-apple-lg overflow-hidden">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Header with Tabs */}
        <div className="bg-gradient-to-r from-background/60 to-background/80 backdrop-blur-sm border-b border-border/10 p-6 pb-0">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center ring-1 ring-primary/10">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-foreground tracking-tight">
                  Inteligencia de Negocio
                </h3>
                <p className="text-sm text-muted-foreground font-medium">
                  Métricas y análisis en tiempo real
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <TabsList className="grid w-full grid-cols-5 bg-background/60 backdrop-blur-sm border border-border/20 rounded-xl p-1 h-auto">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="flex flex-col items-center space-y-2 py-3 px-4 rounded-lg text-xs font-medium transition-all duration-300 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm hover:bg-muted/50"
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab Content */}
        <CardContent className="p-0">
          {tabs.map((tab) => (
            <TabsContent
              key={tab.id}
              value={tab.id}
              className="m-0 p-6 animate-fade-in"
              style={{
                animationDuration: '0.3s',
                animationFillMode: 'both'
              }}
            >
              <div className="w-full">
                {/* Remove the outer card wrapper since we're inside a card already */}
                <div className="[&>div]:border-0 [&>div]:shadow-none [&>div]:bg-transparent [&>div]:backdrop-blur-none">
                  {tab.component}
                </div>
              </div>
            </TabsContent>
          ))}
        </CardContent>
      </Tabs>
    </Card>
  );
}